import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

/**
 * Card — shared surface primitive.
 *
 * Presentation is expressed only through:
 *   - variant (paint): default | muted | primary | success | warning | destructive | info
 *   - size (density):  default | comfortable | compact | flush
 *
 * `success` is an alias of `primary` (same classes).
 * `className` is for layout only (w-*, h-*, flex-*, col-span-*, overflow-hidden, cursor-pointer).
 *
 * See card-variants.proposal.html for the catalog.
 */

type CardSize = 'default' | 'comfortable' | 'compact' | 'flush';

const CardSizeContext = React.createContext<CardSize>('default');

function useCardSize(): CardSize {
  return React.useContext(CardSizeContext);
}

// Primary paint shared by variant="primary" and variant="success"
const primarySurface =
  'border-primary/20 bg-primary/5 text-card-foreground shadow-sm';

const cardVariants = cva(
  'rounded-xl border text-card-foreground',
  {
    variants: {
      variant: {
        default: 'border-border bg-card shadow-sm',
        muted: 'border-border bg-stone-50 shadow-none dark:bg-stone-900/40',
        primary: primarySurface,
        /** Alias of primary — same paint */
        success: primarySurface,
        // An alpha wash over the card, like `primary` and `destructive` above —
        // that is what keeps `text-card-foreground` (and `CardTitle`,
        // `CardDescription`) readable in both themes. The hue lives in the
        // border, which uses the fixed `--warning-border` token (RUT-3732).
        warning: 'border-warning-border bg-warning/10 text-card-foreground shadow-sm',
        destructive:
          'border-destructive/25 bg-destructive/5 text-card-foreground shadow-sm',
        info: 'border-blue-200 bg-blue-50/30 text-card-foreground shadow-sm dark:border-blue-900 dark:bg-blue-950/30',
      },
      size: {
        // size is applied via context to subcomponents; kept on cva for typing only
        default: '',
        comfortable: '',
        compact: '',
        flush: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

// Header / content / footer padding by size.
// Content uses full padding when it's the first child; when it follows a header
// (or label block marked data-slot=card-header), top padding collapses.
const cardHeaderSize: Record<CardSize, string> = {
  default: 'flex flex-col space-y-2 p-6 pb-4',
  comfortable: 'flex flex-col space-y-1.5 px-4 pt-4 pb-3',
  compact: 'flex flex-col space-y-1 p-3 pb-2',
  flush: 'flex flex-col space-y-1 px-4 py-3',
};

const cardContentSize: Record<CardSize, string> = {
  default: 'p-6 [[data-slot=card-header]+&]:pt-0',
  comfortable: 'p-4 [[data-slot=card-header]+&]:pt-0',
  compact: 'p-3 [[data-slot=card-header]+&]:pt-0',
  flush: 'p-0',
};

const cardFooterSize: Record<CardSize, string> = {
  default: 'flex items-center p-6 pt-0',
  comfortable: 'flex items-center px-4 pb-4 pt-0',
  compact: 'flex items-center p-3 pt-0',
  flush: 'flex items-center px-4 py-3',
};

export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, size, ...props }, ref) => {
    const resolvedSize = (size ?? 'default') as CardSize;
    return (
      <CardSizeContext.Provider value={resolvedSize}>
        <div
          ref={ref}
          className={cn(cardVariants({ variant, size: resolvedSize }), className)}
          {...props}
        />
      </CardSizeContext.Provider>
    );
  },
);
Card.displayName = 'Card';

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const size = useCardSize();
  return (
    <div
      ref={ref}
      data-slot="card-header"
      className={cn(cardHeaderSize[size], className)}
      {...props}
    />
  );
});
CardHeader.displayName = 'CardHeader';

const CardTitle = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'text-xl font-bold leading-tight tracking-tight text-foreground',
      className,
    )}
    {...props}
  />
));
CardTitle.displayName = 'CardTitle';

const CardDescription = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
CardDescription.displayName = 'CardDescription';

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const size = useCardSize();
  return (
    <div
      ref={ref}
      className={cn(cardContentSize[size], className)}
      {...props}
    />
  );
});
CardContent.displayName = 'CardContent';

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const size = useCardSize();
  return (
    <div
      ref={ref}
      className={cn(cardFooterSize[size], className)}
      {...props}
    />
  );
});
CardFooter.displayName = 'CardFooter';

/**
 * CardLabel — consistent uppercase label for card headers.
 *
 * Prefer with size="comfortable":
 * ```tsx
 * <Card size="comfortable">
 *   <div className="px-4 pt-4 pb-3">  // or use CardHeader at comfortable
 *     <CardLabel>Title</CardLabel>
 *     <CardLabel variant="subtitle">Optional subtitle</CardLabel>
 *   </div>
 *   <CardContent>…</CardContent>
 * </Card>
 * ```
 */
interface CardLabelProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'subtitle';
}

const CardLabel = React.forwardRef<HTMLDivElement, CardLabelProps>(
  ({ className, variant = 'default', ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        variant === 'default'
          ? 'text-[11px] font-medium uppercase tracking-wider text-stone-400'
          : 'text-[10px] text-stone-400 mt-0.5',
        className,
      )}
      {...props}
    />
  ),
);
CardLabel.displayName = 'CardLabel';

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDescription,
  CardContent,
  CardLabel,
  cardVariants,
  type CardSize,
};
